// Shared wire-id wrappers. Used by every REST route (and the
// `/views/*` composers) that emits entities containing cross-refs to
// other entities — AND by the SPA's mock-data composers, so the
// signed-out / VITE_USE_MOCK_DATA fallback produces byte-identical
// wire shape to the real API. Without the shared helper, the e2e
// Playwright suite renders against raw-id mocks while prod renders
// against prefixed-id payloads — the contract between producer and
// consumer goes untested and bug classes like "matrix all grey for
// the clowder-coffee org" (PR #119) can ship.
//
// Entity ids on the wire carry a public-id prefix (`comp_X`,
// `bizfn_Y`, `engr_Z`, …). Cross-refs nested INSIDE an entity (e.g.
// `derivedContext.functions[]`, `team_change.departures[].engineer_id`)
// must use the SAME prefixed form, or callers can't join across them.
//
// Each helper preserves all other fields verbatim (`...x` spread)
// and only rewrites the id + cross-ref fields it owns. The exported
// surface is intentionally minimal: callers import the entity they
// emit, and only that one.
import { IdPrefix, toPublicId } from './ids.js';
const BIZFN = IdPrefix.business_function;
const COMP = IdPrefix.derivedContext;
const ENG = IdPrefix.engineer;
const PLAN = IdPrefix.plan;
const PROJ = IdPrefix.project;
const RMAP = IdPrefix.roadmap;
const TCHG = IdPrefix.team_change;
/**
 * DerivedContext: id (COMP) + functions[] cross-refs (BIZFN).
 *
 * Skills nested under the derivedContext aren't prefixed — there's no
 * SKILL public-id namespace today, so `comp.skills[*].id` stays raw
 * (and `rating.skill_id` matches it raw).
 */
export function wrapDerivedContext(cmp) {
    return {
        ...cmp,
        id: toPublicId(COMP, cmp.id),
        functions: cmp.functions.map((fnId) => toPublicId(BIZFN, fnId)),
    };
}
/**
 * Project: id (PROJ) + impact.cells (comp + fn cross-refs) +
 * overlay (multiple nested cross-refs) + requirements (skill stays
 * raw since skills aren't prefixed; role is project-local).
 */
export function wrapProject(p) {
    return {
        ...p,
        id: toPublicId(PROJ, p.id),
        impact: {
            cells: p.impact.cells.map((c) => ({
                ...c,
                derivedContext: toPublicId(COMP, c.derivedContext),
                function: toPublicId(BIZFN, c.function),
            })),
        },
        overlay: {
            // Projected new derivedContexts share the COMP id space; their
            // functions[] are projected new functions sharing the BIZFN space.
            new_derived_contexts: p.overlay.new_derived_contexts.map((nc) => ({
                ...nc,
                id: toPublicId(COMP, nc.id),
                functions: nc.functions.map((fnId) => toPublicId(BIZFN, fnId)),
            })),
            new_skills_in_existing: p.overlay.new_skills_in_existing.map((nse) => ({
                ...nse,
                derivedContext: toPublicId(COMP, nse.derivedContext),
            })),
            // Projected new functions share the BIZFN id space.
            new_functions: p.overlay.new_functions.map((nf) => ({
                ...nf,
                id: toPublicId(BIZFN, nf.id),
                ...(nf.derivedContexts !== undefined
                    ? { derivedContexts: nf.derivedContexts.map((cid) => toPublicId(COMP, cid)) }
                    : {}),
            })),
            // Keys are function ids; values are arrays of derivedContext ids.
            new_function_derived_contexts: Object.fromEntries(Object.entries(p.overlay.new_function_derived_contexts).map(([fnId, compIds]) => [
                toPublicId(BIZFN, fnId),
                compIds.map((cid) => toPublicId(COMP, cid)),
            ])),
        },
    };
}
/**
 * TeamChange: id (TCHG) + engineer cross-refs (departures,
 * upskills, hires). Hire ids share the ENG namespace — once a hire
 * lands the projected id becomes a real engineer.
 *
 * Skill ids in upskills + hire ratings stay raw (no SKILL prefix).
 */
export function wrapTeamChange(tc) {
    return {
        ...tc,
        id: toPublicId(TCHG, tc.id),
        departures: tc.departures.map((d) => ({
            ...d,
            engineer_id: toPublicId(ENG, d.engineer_id),
        })),
        upskills: tc.upskills.map((u) => ({
            ...u,
            engineer_id: toPublicId(ENG, u.engineer_id),
        })),
        hires: tc.hires.map((h) => ({ ...h, id: toPublicId(ENG, h.id) })),
    };
}
/**
 * Roadmap: id (RMAP) + team_change (PLAN, kind='team_change') +
 * projects[] (PLAN, kind='project').
 *
 * Both cross-refs now resolve against the plans table, so they get
 * the `plan_` prefix on the wire. The field names stay (`team_change`
 * / `projects`) — the cardinality and roadmap-side UX are unchanged,
 * only the namespace of the referenced ids shifted from `tchg_*` /
 * `proj_*` to `plan_*`.
 */
export function wrapRoadmap(rm) {
    return {
        ...rm,
        id: toPublicId(RMAP, rm.id),
        team_change: rm.team_change != null ? toPublicId(PLAN, rm.team_change) : null,
        projects: rm.projects.map((pid) => toPublicId(PLAN, pid)),
    };
}
/**
 * Plan: id (PLAN). Payload is opaque scaffold state — no nested
 * cross-refs use prefixed ids today (roles, engineers in payload are
 * scaffold-local, skills are raw, comp ids are raw to match the rest
 * of the SKILL/COMP namespace policy).
 */
export function wrapPlan(p) {
    return { ...p, id: toPublicId(PLAN, p.id) };
}
/**
 * ActivityItem: no id of its own to wrap (sha is the natural key),
 * but each `cells[]` entry has `derivedContext` + `function` cross-refs
 * that must use the prefixed form. Without this, the Activity
 * heatmap silently renders as a sparse N/A grid for any prod org —
 * the same cross-ref namespace bug as `wrapDerivedContext`'s `functions[]`,
 * just on a different entity.
 */
export function wrapActivityItem(item) {
    return {
        ...item,
        cells: item.cells.map((c) => ({
            ...c,
            derivedContext: toPublicId(COMP, c.derivedContext),
            function: toPublicId(BIZFN, c.function),
        })),
    };
}
//# sourceMappingURL=wire-helpers.js.map